import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Package, PencilSimple, Trash, Eye, EyeSlash, X, Gear, UploadSimple, Star, FileCsv } from "@phosphor-icons/react";
import { api, photoHref } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATEGORIES = ["Base", "Wall", "Tall", "Vanity", "Accessory"];
const HINGES = [["N", "N/A"], ["L", "Left"], ["R", "Right"]];
const EMPTY = {
  code: "", door_style: "", category: "Base", name: "", width_in: "", height_in: "",
  depth_in: "", box_color: "", hinge: "N", price: "", qty: 0, low_stock: 0, notes: "",
  photo: "", public_visible: false,
};

const fieldCls =
  "mt-1 h-10 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none";
const Label = ({ children }) => (
  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{children}</span>
);

export default function CabinetryInventory() {
  const [items, setItems] = useState([]);
  const [styles, setStyles] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [doorStyle, setDoorStyle] = useState("All");
  const [hidden, setHidden] = useState(false);
  const [lowStock, setLowStock] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [stylesOpen, setStylesOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (category !== "All") params.category = category;
    if (doorStyle !== "All") params.door_style = doorStyle;
    if (hidden) params.hidden = true;
    if (lowStock) params.low_stock = true;
    try {
      const { data } = await api.get("/cabinets", { params });
      setItems(data);
    } catch {
      toast.error("Could not load cabinets");
    }
  }, [q, category, doorStyle, hidden, lowStock]);

  const loadStyles = useCallback(async () => {
    try {
      const { data } = await api.get("/cabinet-door-styles");
      setStyles(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadStyles();
  }, [loadStyles]);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (c) => {
    const clean = {};
    Object.entries(c).forEach(([k, v]) => {
      clean[k] = v === null ? "" : v;
    });
    setForm({ ...EMPTY, ...clean });
    setEditing(c.id);
  };

  const save = async () => {
    if (!form.code.trim()) {
      toast.error("Enter a code / SKU");
      return;
    }
    const payload = {
      ...form,
      width_in: form.width_in === "" ? null : Number(form.width_in),
      height_in: form.height_in === "" ? null : Number(form.height_in),
      depth_in: form.depth_in === "" ? null : Number(form.depth_in),
      price: form.price === "" ? null : Number(form.price),
      qty: Number(form.qty) || 0,
      low_stock: Number(form.low_stock) || 0,
    };
    try {
      if (editing === "new") await api.post("/cabinets", payload);
      else await api.patch(`/cabinets/${editing}`, payload);
      toast.success(`Cabinet ${form.code} saved`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save cabinet");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.code}?`)) return;
    await api.delete(`/cabinets/${c.id}`);
    toast.success(`${c.code} deleted`);
    load();
  };

  const toggleVisible = async (c) => {
    const next = c.public_visible === false;
    const { data } = await api.patch(`/cabinets/${c.id}/flags`, { public_visible: next });
    setItems((prev) => prev.map((x) => (x.id === c.id ? data : x)));
    toast.success(next ? "Visible on public & portal" : "Hidden from customers");
  };

  const refreshCabinet = (data) => {
    setForm((f) => ({ ...f, photos: data.photos || [], photo: data.photo || "" }));
    setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || editing === "new") return;
    setPhotoBusy(true);
    try {
      let data;
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        ({ data } = await api.post(`/cabinets/${editing}/photos`, fd));
      }
      if (data) refreshCabinet(data);
      toast.success(`Photo${files.length > 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setPhotoBusy(false);
  };

  const deletePhoto = async (photoId) => {
    const { data } = await api.delete(`/cabinets/${editing}/photos/${photoId}`);
    refreshCabinet(data);
  };

  const makePrimary = async (photoId) => {
    const { data } = await api.patch(`/cabinets/${editing}/photos/${photoId}/primary`);
    refreshCabinet(data);
    toast.success("Primary photo set");
  };

  const prefix = styles.find((s) => s.name === form.door_style)?.prefix;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Package size={24} weight="bold" className="text-indigo-400" /> Cabinetry
          </h1>
          <div className="flex gap-2">
            <Button asChild variant="outline" data-testid="cabinet-import-btn">
              <Link to="/inventory/cabinetry/import">
                <FileCsv size={16} weight="bold" className="mr-1.5" /> Import CSV
              </Link>
            </Button>
            <Button
              variant="outline"
              data-testid="cabinet-door-styles-btn"
              onClick={() => setStylesOpen(true)}
            >
              <Gear size={16} weight="bold" className="mr-1.5" /> Door styles
            </Button>
            <Button data-testid="cabinet-add-btn" onClick={openNew}>
              <Plus size={16} weight="bold" className="mr-1.5" /> Add cabinet
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Input
            data-testid="cabinet-search"
            placeholder="Search code, name, colour…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10 w-full max-w-xs"
          />
          <select
            data-testid="cabinet-filter-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-10 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm"
          >
            {["All", ...CATEGORIES].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <select
            data-testid="cabinet-filter-style"
            value={doorStyle}
            onChange={(e) => setDoorStyle(e.target.value)}
            className="h-10 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm"
          >
            <option>All</option>
            {styles.map((s) => (
              <option key={s.id}>{s.name}</option>
            ))}
          </select>
          <button
            data-testid="cabinet-filter-hidden"
            onClick={() => setHidden((v) => !v)}
            className={`h-10 rounded-lg border px-3 text-xs font-bold uppercase tracking-widest ${
              hidden ? "border-zinc-500 bg-zinc-800 text-zinc-200" : "border-zinc-800 text-zinc-500"
            }`}
          >
            Hidden
          </button>
          <button
            data-testid="cabinet-filter-lowstock"
            onClick={() => setLowStock((v) => !v)}
            className={`h-10 rounded-lg border px-3 text-xs font-bold uppercase tracking-widest ${
              lowStock ? "border-rose-500 bg-rose-500/15 text-rose-300" : "border-zinc-800 text-zinc-500"
            }`}
          >
            Low stock
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="cabinet-list">
          {items.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-zinc-800 p-8 text-center text-zinc-500" data-testid="cabinet-empty">
              No cabinets yet. Click “Add cabinet” to create one.
            </div>
          )}
          {items.map((c) => {
            const low = (c.low_stock || 0) > 0 && (c.qty || 0) <= (c.low_stock || 0);
            return (
              <div key={c.id} data-testid={`cabinet-card-${c.id}`} className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono-data text-sm font-bold text-indigo-300">{c.code}</div>
                    <div className="truncate text-sm font-semibold text-zinc-100">{c.name || c.door_style || "Cabinet"}</div>
                    <div className="text-xs text-zinc-500">
                      {c.category}
                      {c.door_style ? ` · ${c.door_style}` : ""}
                    </div>
                  </div>
                  {c.public_visible === false && (
                    <span data-testid={`cabinet-hidden-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-zinc-600 bg-zinc-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-300">
                      <EyeSlash size={10} weight="bold" /> Hidden
                    </span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
                  {(c.width_in || c.height_in || c.depth_in) && (
                    <span>{c.width_in || "?"}" × {c.height_in || "?"}" × {c.depth_in || "?"}"</span>
                  )}
                  {c.box_color && <span>{c.box_color}</span>}
                  {c.hinge && c.hinge !== "N" && <span>Hinge {c.hinge}</span>}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono-data text-lg font-bold text-yellow-400">
                    {c.price != null ? `$${Number(c.price).toFixed(2)}` : "—"}
                  </span>
                  <span data-testid={`cabinet-qty-${c.id}`} className={`text-sm font-semibold ${low ? "text-rose-400" : "text-zinc-300"}`}>
                    {c.qty || 0} on hand{low ? " · LOW" : ""}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2 border-t border-zinc-800 pt-3">
                  <button data-testid={`cabinet-visibility-${c.id}`} onClick={() => toggleVisible(c)} className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest ${c.public_visible === false ? "border-zinc-700 text-zinc-400" : "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"}`}>
                    {c.public_visible === false ? <><EyeSlash size={13} weight="bold" /> Hidden</> : <><Eye size={13} weight="bold" /> Shown</>}
                  </button>
                  <button data-testid={`cabinet-edit-${c.id}`} onClick={() => openEdit(c)} className="flex h-8 w-9 items-center justify-center rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button data-testid={`cabinet-delete-${c.id}`} onClick={() => remove(c)} className="flex h-8 w-9 items-center justify-center rounded-lg border border-zinc-700 text-rose-400 hover:bg-rose-500/10">
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cabinet-dialog">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditing(null)} />
          <div className="relative z-10 max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-bold uppercase tracking-widest text-indigo-300">
                {editing === "new" ? "Add cabinet" : "Edit cabinet"}
              </div>
              <button data-testid="cabinet-dialog-close" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-zinc-200">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-1">
                <Label>Code / SKU *</Label>
                <input data-testid="cabinet-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder={prefix ? `${prefix}0001` : "e.g. SW-0001"} className={fieldCls} />
              </label>
              <label className="col-span-1">
                <Label>Door style{prefix ? ` (${prefix})` : ""}</Label>
                <select data-testid="cabinet-form-style" value={form.door_style} onChange={(e) => setForm({ ...form, door_style: e.target.value })} className={fieldCls}>
                  <option value="">—</option>
                  {styles.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </label>
              <label className="col-span-1">
                <Label>Category</Label>
                <select data-testid="cabinet-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={fieldCls}>
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label className="col-span-1">
                <Label>Name / label</Label>
                <input data-testid="cabinet-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} />
              </label>
              <label><Label>Width (in)</Label><input type="number" data-testid="cabinet-form-width" value={form.width_in} onChange={(e) => setForm({ ...form, width_in: e.target.value })} className={fieldCls} /></label>
              <label><Label>Height (in)</Label><input type="number" data-testid="cabinet-form-height" value={form.height_in} onChange={(e) => setForm({ ...form, height_in: e.target.value })} className={fieldCls} /></label>
              <label><Label>Depth (in)</Label><input type="number" data-testid="cabinet-form-depth" value={form.depth_in} onChange={(e) => setForm({ ...form, depth_in: e.target.value })} className={fieldCls} /></label>
              <label><Label>Box colour / material</Label><input data-testid="cabinet-form-box" value={form.box_color} onChange={(e) => setForm({ ...form, box_color: e.target.value })} className={fieldCls} /></label>
              <label>
                <Label>Hinge / side</Label>
                <select data-testid="cabinet-form-hinge" value={form.hinge} onChange={(e) => setForm({ ...form, hinge: e.target.value })} className={fieldCls}>
                  {HINGES.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>
              <label><Label>Price each ($)</Label><input type="number" data-testid="cabinet-form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={fieldCls} /></label>
              <label><Label>Qty on hand</Label><input type="number" data-testid="cabinet-form-qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className={fieldCls} /></label>
              <label><Label>Low-stock threshold</Label><input type="number" data-testid="cabinet-form-lowstock" value={form.low_stock} onChange={(e) => setForm({ ...form, low_stock: e.target.value })} className={fieldCls} /></label>
              <label className="col-span-2"><Label>Notes</Label><input data-testid="cabinet-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={fieldCls} /></label>
              <label className="col-span-2 flex items-center gap-2 pt-1">
                <input type="checkbox" data-testid="cabinet-form-visible" checked={form.public_visible} onChange={(e) => setForm({ ...form, public_visible: e.target.checked })} className="h-4 w-4 accent-indigo-500" />
                <span className="text-xs text-zinc-300">Show on public site &amp; customer portal</span>
              </label>
            </div>

            <div className="mt-4 border-t border-zinc-800 pt-4" data-testid="cabinet-photos">
              <div className="flex items-center justify-between">
                <Label>Photos / door style</Label>
                {editing !== "new" && (
                  <Button
                    variant="outline"
                    disabled={photoBusy}
                    data-testid="cabinet-photo-upload-btn"
                    onClick={() => fileRef.current?.click()}
                    className="h-8 text-xs"
                  >
                    <UploadSimple size={14} weight="bold" className="mr-1.5" />
                    {photoBusy ? "Uploading…" : "Add photos"}
                  </Button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="cabinet-photo-input"
                  onChange={uploadPhotos}
                />
              </div>
              {editing === "new" ? (
                <div className="mt-2 text-xs text-zinc-500">
                  Save the cabinet first, then reopen it to add photos.
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap gap-3">
                  {(form.photos || []).length === 0 && (
                    <div className="text-xs text-zinc-500" data-testid="cabinet-photos-empty">
                      No photos yet. Add the door-style image customers will see.
                    </div>
                  )}
                  {(form.photos || []).map((p) => (
                    <div
                      key={p.id}
                      data-testid={`cabinet-photo-${p.id}`}
                      className="group relative h-24 w-24 overflow-hidden rounded-lg border border-zinc-800"
                    >
                      <img
                        src={photoHref(p.thumb_path || p.storage_path)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                      {p.primary && (
                        <span
                          data-testid={`cabinet-photo-primary-${p.id}`}
                          className="absolute left-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-indigo-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white"
                        >
                          <Star size={9} weight="fill" /> Main
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 flex opacity-0 transition-opacity group-hover:opacity-100">
                        {!p.primary && (
                          <button
                            data-testid={`cabinet-photo-make-primary-${p.id}`}
                            onClick={() => makePrimary(p.id)}
                            className="flex-1 bg-black/70 py-1 text-[9px] font-bold uppercase text-indigo-300 hover:bg-black/90"
                          >
                            Main
                          </button>
                        )}
                        <button
                          data-testid={`cabinet-photo-delete-${p.id}`}
                          onClick={() => deletePhoto(p.id)}
                          className="flex-1 bg-black/70 py-1 text-[9px] font-bold uppercase text-rose-400 hover:bg-black/90"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="flex-1" data-testid="cabinet-form-save" onClick={save}>Save</Button>
            </div>
          </div>
        </div>
      )}

      {stylesOpen && (
        <DoorStyleManager styles={styles} onClose={() => setStylesOpen(false)} reload={loadStyles} />
      )}
    </div>
  );
}

const DoorStyleManager = ({ styles, onClose, reload }) => {
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/cabinet-door-styles", { name: name.trim(), prefix: prefix.trim() });
      setName("");
      setPrefix("");
      reload();
      toast.success("Door style added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
  };
  const del = async (id) => {
    await api.delete(`/cabinet-door-styles/${id}`);
    reload();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="door-style-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-bold uppercase tracking-widest text-indigo-300">Door styles</div>
          <button data-testid="door-style-close" onClick={onClose} className="text-zinc-400 hover:text-zinc-200"><X size={18} weight="bold" /></button>
        </div>
        <div className="flex gap-2">
          <input data-testid="door-style-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Shaker White" className={fieldCls} />
          <input data-testid="door-style-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="SW-" className={`${fieldCls} max-w-[90px]`} />
          <Button data-testid="door-style-add" onClick={add}>Add</Button>
        </div>
        <div className="mt-3 space-y-2">
          {styles.length === 0 && <div className="text-sm text-zinc-500">No door styles yet.</div>}
          {styles.map((s) => (
            <div key={s.id} data-testid={`door-style-${s.id}`} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
              <div className="text-sm text-zinc-200">{s.name} <span className="font-mono-data text-zinc-500">{s.prefix}</span></div>
              <button data-testid={`door-style-delete-${s.id}`} onClick={() => del(s.id)} className="text-rose-400 hover:text-rose-300"><Trash size={14} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
